/**
 * Dashboard metric calculations
 * Utility functions for calculating dashboard KPIs
 */

/**
 * Calculate collection rate (percentage of invoices paid on time)
 */
export function calculateCollectionRate(invoices) {
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  if (paidInvoices.length === 0) return 0;
  
  const paidOnTime = paidInvoices.filter(inv => {
    const paidDate = new Date(inv.updated_at);
    const dueDate = new Date(inv.due_date);
    return paidDate <= dueDate;
  });
  
  return (paidOnTime.length / paidInvoices.length) * 100;
}

/**
 * Calculate average payment time in days
 */
export function calculateAveragePaymentTime(invoices) {
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  if (paidInvoices.length === 0) return 0;
  
  const totalDays = paidInvoices.reduce((sum, inv) => {
    const sentDate = new Date(inv.created_at);
    const paidDate = new Date(inv.updated_at);
    const days = Math.floor((paidDate - sentDate) / (1000 * 60 * 60 * 24));
    return sum + days;
  }, 0);
  
  return Math.round(totalDays / paidInvoices.length);
}

/**
 * Calculate payment success rate
 */
export function calculatePaymentSuccessRate(invoices) {
  const sentInvoices = invoices.filter(inv => inv.status !== 'draft');
  if (sentInvoices.length === 0) return 0;
  
  const successfulPayments = invoices.filter(inv => 
    inv.status === 'paid' || inv.status === 'partially_paid'
  );
  
  return (successfulPayments.length / sentInvoices.length) * 100;
}

/**
 * Calculate revenue growth (month over month)
 */
export function calculateRevenueGrowth(invoices) {
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  
  let revenueThisMonth = 0;
  let revenueLastMonth = 0;
  
  invoices.forEach(invoice => {
    if (invoice.status === 'paid') {
      const paidDate = new Date(invoice.updated_at);
      if (paidDate >= thisMonthStart) {
        revenueThisMonth += invoice.total;
      }
      if (paidDate >= lastMonthStart && paidDate <= lastMonthEnd) {
        revenueLastMonth += invoice.total;
      }
    }
  });
  
  if (revenueLastMonth === 0) return revenueThisMonth > 0 ? 100 : 0;
  
  return ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100;
}

/**
 * Generate sparkline data for last N days
 */
export function generateSparklineData(invoices, days = 7, metric = 'revenue') {
  const data = [];
  const now = new Date();
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);
    
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);
    
    let value = 0;
    
    if (metric === 'revenue') {
      value = invoices
        .filter(inv => {
          const paidDate = new Date(inv.updated_at);
          return inv.status === 'paid' && paidDate >= date && paidDate < nextDate;
        })
        .reduce((sum, inv) => sum + inv.total, 0);
    } else if (metric === 'count') {
      value = invoices.filter(inv => {
        const createdDate = new Date(inv.created_at);
        return createdDate >= date && createdDate < nextDate;
      }).length;
    }
    
    data.push(value);
  }
  
  return data;
}

/**
 * Calculate overdue amount by aging
 */
export function calculateOverdueAging(invoices) {
  const now = new Date();
  const aging = {
    '0-30': 0,
    '31-60': 0,
    '61-90': 0,
    '90+': 0,
  };
  
  invoices.forEach(invoice => {
    if ((invoice.status === 'sent' || invoice.status === 'partially_paid') && invoice.balance_due > 0) {
      const dueDate = new Date(invoice.due_date);
      if (dueDate < now) {
        const daysOverdue = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
        
        if (daysOverdue <= 30) {
          aging['0-30'] += invoice.balance_due;
        } else if (daysOverdue <= 60) {
          aging['31-60'] += invoice.balance_due;
        } else if (daysOverdue <= 90) {
          aging['61-90'] += invoice.balance_due;
        } else {
          aging['90+'] += invoice.balance_due;
        }
      }
    }
  });
  
  return aging;
}

/**
 * Generate revenue trend data for last N months
 */
export function generateRevenueTrendData(invoices, months = 6) {
  const data = [];
  const now = new Date();
  
  for (let i = months - 1; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    
    const revenue = invoices
      .filter(inv => {
        const paidDate = new Date(inv.updated_at);
        return inv.status === 'paid' && paidDate >= monthDate && paidDate < nextMonth;
      })
      .reduce((sum, inv) => sum + inv.total, 0);
    
    data.push({
      label: monthDate.toLocaleDateString('en-IN', { month: 'short' }),
      value: revenue,
    });
  }
  
  return data;
}
